import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Mail,
  MailX,
  Search,
  RefreshCw,
  Users,
  CheckCircle,
  XCircle,
  Save,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CustomerEmailPreferences() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [pendingChanges, setPendingChanges] = useState({});
  const [filter, setFilter] = useState("all"); // all | on | off | has-email

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/customers/email-preferences`);
      setCustomers(data);
      setPendingChanges({});
    } catch {
      toast.error("Failed to load customers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  const toggleAutoEmail = (customerId, currentValue) => {
    setPendingChanges((prev) => {
      const original = customers.find((c) => c.id === customerId);
      const newVal = !currentValue;
      if (original && original.auto_email === newVal) {
        const copy = { ...prev };
        delete copy[customerId];
        return copy;
      }
      return { ...prev, [customerId]: newVal };
    });
  };

  const getEffectiveValue = (customer) => {
    if (customer.id in pendingChanges) return pendingChanges[customer.id];
    return customer.auto_email;
  };

  const saveChanges = async () => {
    const entries = Object.entries(pendingChanges);
    if (entries.length === 0) return;
    setSaving(true);
    try {
      await axios.put(`${API}/customers/email-preferences/bulk`, {
        updates: entries.map(([customer_id, auto_email]) => ({
          customer_id,
          auto_email,
        })),
      });
      toast.success(`Updated ${entries.length} customer(s)`);
      fetchCustomers();
    } catch {
      toast.error("Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  const filtered = useMemo(() => {
    let list = customers;
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(
        (c) =>
          (c.name || "").toLowerCase().includes(q) ||
          (c.email || "").toLowerCase().includes(q) ||
          (c.account_number || "").toLowerCase().includes(q)
      );
    }
    if (filter === "on") list = list.filter((c) => getEffectiveValue(c));
    if (filter === "off") list = list.filter((c) => !getEffectiveValue(c));
    if (filter === "has-email") list = list.filter((c) => c.email);
    return list;
  }, [customers, search, filter, pendingChanges]);

  const stats = useMemo(() => {
    const withEmail = customers.filter((c) => c.email).length;
    const autoOn = customers.filter((c) => getEffectiveValue(c)).length;
    const autoOff = customers.length - autoOn;
    return { total: customers.length, withEmail, autoOn, autoOff };
  }, [customers, pendingChanges]);

  const changesCount = Object.keys(pendingChanges).length;

  return (
    <div className="p-6 space-y-6" data-testid="email-preferences-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" data-testid="email-prefs-title">
            Customer Email Preferences
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Control which customers receive automatic invoice emails
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={fetchCustomers}
            disabled={loading}
            data-testid="refresh-email-prefs"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          {changesCount > 0 && (
            <Button
              onClick={saveChanges}
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="save-email-prefs"
            >
              <Save className="h-4 w-4 mr-2" />
              Save {changesCount} Change{changesCount > 1 ? "s" : ""}
            </Button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="cursor-pointer hover:ring-2 ring-gray-300" onClick={() => setFilter("all")}>
          <CardContent className="p-4 text-center">
            <Users className="h-5 w-5 mx-auto mb-1 text-gray-600" />
            <div className="text-2xl font-bold text-gray-800">{stats.total}</div>
            <div className="text-xs text-gray-500">Total Customers</div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:ring-2 ring-blue-300" onClick={() => setFilter("has-email")}>
          <CardContent className="p-4 text-center">
            <Mail className="h-5 w-5 mx-auto mb-1 text-blue-600" />
            <div className="text-2xl font-bold text-blue-800">{stats.withEmail}</div>
            <div className="text-xs text-blue-600">Have Email</div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:ring-2 ring-green-300" onClick={() => setFilter("on")}>
          <CardContent className="p-4 text-center">
            <CheckCircle className="h-5 w-5 mx-auto mb-1 text-green-600" />
            <div className="text-2xl font-bold text-green-800">{stats.autoOn}</div>
            <div className="text-xs text-green-600">Auto-Email ON</div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:ring-2 ring-red-300" onClick={() => setFilter("off")}>
          <CardContent className="p-4 text-center">
            <XCircle className="h-5 w-5 mx-auto mb-1 text-red-500" />
            <div className="text-2xl font-bold text-red-700">{stats.autoOff}</div>
            <div className="text-xs text-red-500">Auto-Email OFF</div>
          </CardContent>
        </Card>
      </div>

      {/* Search & Filter */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search by name, email or account..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="email-prefs-search"
          />
        </div>
        <div className="flex gap-1">
          {["all", "on", "off", "has-email"].map((f) => (
            <Button
              key={f}
              variant={filter === f ? "default" : "outline"}
              size="sm"
              onClick={() => setFilter(f)}
              className={filter === f ? "bg-gray-800" : ""}
              data-testid={`filter-${f}`}
            >
              {f === "all" ? "All" : f === "on" ? "ON" : f === "off" ? "OFF" : "Has Email"}
            </Button>
          ))}
        </div>
        <span className="text-sm text-gray-500">{filtered.length} shown</span>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-12 text-center">
              <RefreshCw className="h-8 w-8 animate-spin mx-auto text-gray-400" />
            </div>
          ) : (
            <div className="max-h-[60vh] overflow-y-auto">
              <Table>
                <TableHeader className="sticky top-0 bg-white z-10">
                  <TableRow>
                    <TableHead className="w-12">#</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Account</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead className="text-center w-40">Auto-Email</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-12 text-gray-400">
                        <MailX className="h-10 w-10 mx-auto mb-2 opacity-40" />
                        No customers match your filter
                      </TableCell>
                    </TableRow>
                  ) : (
                    filtered.map((c, i) => {
                      const isOn = getEffectiveValue(c);
                      const hasChange = c.id in pendingChanges;
                      return (
                        <TableRow
                          key={c.id}
                          className={hasChange ? "bg-amber-50" : ""}
                          data-testid={`customer-row-${c.id}`}
                        >
                          <TableCell className="text-gray-400 text-xs">{i + 1}</TableCell>
                          <TableCell className="font-medium">{c.name}</TableCell>
                          <TableCell className="text-gray-500 text-sm font-mono">
                            {c.account_number || "-"}
                          </TableCell>
                          <TableCell className="text-sm">
                            {c.email ? (
                              <span className="text-blue-600">{c.email}</span>
                            ) : (
                              <span className="text-gray-300 italic">No email</span>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            <div className="flex items-center justify-center gap-2">
                              <Switch
                                checked={isOn}
                                onCheckedChange={() => toggleAutoEmail(c.id, isOn)}
                                data-testid={`toggle-${c.id}`}
                              />
                              <span
                                className={`text-xs font-medium ${isOn ? "text-green-600" : "text-red-500"}`}
                              >
                                {isOn ? "ON" : "OFF"}
                              </span>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
